import { type RefObject, useEffect, useRef } from 'react';

import type { ListboxOptionItem } from './listbox.constant';

/** 목록 id 와 줄 번호로 줄의 id 를 만든다(`aria-activedescendant` 가 가리킨다) */
export function listboxOptionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`;
}

/**
 * 가리킨 줄을 옮길 때 다음 줄을 찾는다. disabled 줄은 건너뛴다. 끝에서 멈춘다(돌아가지 않는다).
 * - `step` 1 = 아래, -1 = 위
 * - `from` 이 null 이면 아래는 첫 줄, 위는 마지막 줄부터 찾는다
 * - 갈 곳이 없으면 `from` 을 그대로 돌려준다
 */
export function findEnabledValue(
  options: readonly ListboxOptionItem[],
  from: string | null,
  step: 1 | -1,
): string | null {
  const current = from === null ? -1 : options.findIndex((option) => option.value === from);
  let index = current === -1 ? (step === 1 ? 0 : options.length - 1) : current + step;
  while (index >= 0 && index < options.length) {
    const option = options[index];
    if (option && !option.disabled) return option.value;
    index += step;
  }
  return from;
}

/** 처음 또는 마지막으로 고를 수 있는 줄 */
export function edgeEnabledValue(
  options: readonly ListboxOptionItem[],
  edge: 'first' | 'last',
): string | null {
  return findEnabledValue(options, null, edge === 'first' ? 1 : -1);
}

/** 한 줄을 골랐을 때 다음 선택 값들. single 은 그 줄만, multiple 은 넣거나 뺀다 */
export function toggleValue(
  values: readonly string[],
  value: string,
  selection: 'single' | 'multiple',
): string[] {
  if (selection === 'single') return [value];
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

/**
 * 열린 목록 밖을 누르면 닫는다. `refs` 안쪽(트리거 · 떠 있는 목록)을 누른 것은 무시한다.
 */
export function useDismissOnOutsidePress(
  enabled: boolean,
  refs: ReadonlyArray<RefObject<HTMLElement | null>>,
  onDismiss: () => void,
): void {
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  });
  useEffect(() => {
    if (!enabled) return;
    const handle = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (refs.some((ref) => ref.current?.contains(target))) return;
      onDismissRef.current();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
    // refs 는 useRef 객체라 바뀌지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
